// 👇 Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// 👇 Axios Imports
import axios from 'axios'
import { urlAuthService, urlEquipmentScheduleService, urlReportingService, urlFreightForwardService, urlCargoInventoryService, urlMaterialInventoryService, urlConfigCacheService, urlCleanlinessCertificate} from '../../../../endpoints'
import { getUriParams } from '@utils'
// Get User Data from localStorage or passed userData
const getUserData = (userData) => {
    return userData || JSON.parse(localStorage.getItem("userData"))
}

const getCompanyCode = (userData) => {
    const user = getUserData(userData)
    if (user.companyCode === "WTC") {
        // WTC admin: use the selected company code if one is set, otherwise return "" (show all)
        return user.selectedCompanyCode || ""
    }
    return user.companyCode
}

export const getStuffingReportStats = async (userData)  => {
    const customerDashboardDTO = {
        fromDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString(), // 6 months ago
        toDate: new Date().toISOString(),
        customerCode: getCompanyCode(userData)
    }
    const response = await axios.post(`${urlReportingService}/GetStuffingReportStats`, customerDashboardDTO)
    return {
        data: response.data
    }
}
// Transloading Tab
export const GetAllCustomerTransloadReportList = createAsyncThunk('customerDashboard/GetAllCustomerTransloadReportList', async (data) => {
    const { params, filter, userData } = data // ADDED: userData

    const uri = getUriParams(params)
    const filterData = {
        customerCode: getCompanyCode(userData),
        isValid: true, 
        oceanBookingNumber: filter.bookingNumber || "",
        customerReference: filter.customerReference || "",
        shipperReference: filter.shipperReference || "",
        vesselName: filter.vesselName || "",
        voyageNumber: filter.voyageNumber || "",
        destination: filter.destination || "",
        shippingLineCode: filter.line || "",
        lrdFrom: filter.lrdFrom || null,
        lrdTo: filter.lrdTo || null
    }
    console.log("GetAllCustomerTransloadReportList using companyCode:", getCompanyCode(userData))
    const response = await axios.post(`${urlReportingService}/GetAllCustomerTransloadReportList?${uri}`, filterData)
    return {
        params,
        data: response.data.data,
        totalPages: response.data.totalCount,
        filterData
    }
})

// Customer Cleanliness Certificate Tab
export const GetAllCustomerCleanlinessCertificateList = createAsyncThunk('customerDashboard/GetAllCustomerCleanlinessCertificateList', async (data) => {
    const { params, filter, userData } = data

    const uri = getUriParams(params)
    const filterData = {
        customer: getCompanyCode(userData),
        transloadOrderNumber: filter.transloadOrderNumber || "",
        bookingNumber: filter.bookingNumber || "",
        vesselName: filter.vesselName || "",
        voyageNumber: filter.voyageNumber || "",
        terminalCode: filter.terminalCode || ""
    }
    const response = await axios.post(`${urlCleanlinessCertificate}/GetAllCustomerCleanlinessCertificateList?${uri}`, filterData)
    return {
        params,
        data: response.data.data,
        totalPages: response.data.totalCount,
        filterData
    }
})

// Customer Certificates Preview - Cleanliness
export const GetCustomerCleanlinessCertificate = createAsyncThunk('customerDashboard/GetCustomerCleanlinessCertificate', async ({ transloadOrderNumber, bookingNumber }) => {
    const response = await axios.get(`${urlCleanlinessCertificate}/GetCustomerCleanlinessCertificate/${transloadOrderNumber}/${bookingNumber}`)
    return {
        data: response.data.data
    }
})

// Customer Certificates Preview - Weight
export const GetCustomerWeightCertificate = createAsyncThunk('customerDashboard/GetCustomerWeightCertificate', async ({ transloadOrderNumber, bookingNumber }) => {
    const response = await axios.get(`${urlCleanlinessCertificate}/GetCustomerWeightCertificate/${transloadOrderNumber}/${bookingNumber}`)
    return {
        data: response.data.data
    }
})

// Freight Forwarding Tab
export const getFFOrderList = createAsyncThunk('customerDashboard/getFFOrderList', async (data) => {
    const { params, filter, userData } = data // ADDED: userData

    const uri = getUriParams(params)
    const filterData = {
        companyCode: getCompanyCode(userData),
        ffNumber: filter.ffNumber || "",
        oceanBookingNumber: filter.bookingNumber || "",
        shipperRefNumber: filter.shipperReference || "",
        source: filter.source || "",
        destination: filter.destination || "",
        vesselName: filter.vesselName || "",
        voyageNumber: filter.voyageNumber || "",
        fromETDPortOfLoading: filter.frometd || null,
        toETDPortOfLoading: filter.toetd || null,
        fromETAPortOfDestination: filter.frometa || null,
        toETAPortOfDestination: filter.toeta || null,
        IsCompletedBooking: filter.isCompletedBooking
       
    }
    const response = await axios.post(`${urlFreightForwardService}/GetAllFFRequest?${uri}`, filterData)
    return {
        params,
        data: response.data.data,
        totalPages: response.data.totalCount,
        filterData
    }
})

// Cargo Inventory Tab
export const getCargoInventory = createAsyncThunk('customerDashboard/getCargoInventory', async (data) => {
   const { params, filter, userData } = data // ADDED: userData

    const uri = getUriParams(params)
    const filterData = {
        isActive: true, // Always set to true
        customer: getCompanyCode(userData),
        commodityType: filter.commodityType || "",
        commodityName: filter.commodityName || "",
        commodityGrade: filter.commodityGrade || "",
        lotNumber: filter.lotNumber || "",
        packagingType: filter.packagingType || "",
        yardLocation: filter.yardLocation || "",
        locationType: filter.locationType || "",
        fromDateIn: filter.fromDateIn || null,
        toDateIn: filter.toDateIn || null
    }
    const response = await axios.post(`${urlCargoInventoryService}/GetAllCargoInventory?${uri}`, filterData)
    return {
        params,
        data: response.data.data,
        totalPages: response.data.totalCount,
        filterData
    }
})

// Material Inventory Tab
export const getMaterialInventory = createAsyncThunk('customerDashboard/getMaterialInventory', async (data) => {
   const { params, filter, userData } = data // ADDED: userData

    const uri = getUriParams(params)
    const filterData = {
        isActive: true, // Always set to true
        owner: getCompanyCode(userData),
        type: "PACKAGING",
        item: filter.item || "",
        yardLocation: filter.yardLocation || null,
        locationType: filter.locationType || null,
        locationCode: filter.locationCode || "",
        reservedItems: filter.reservedItems || null
    }
    const response = await axios.post(`${urlMaterialInventoryService}/GetAllMaterialInventory?${uri}`, filterData)
    return {
        params,
        data: response.data.data,
        totalPages: response.data.totalCount,
        filterData
    }
})

// ** USE FOR - Inbound Cargo Tab
export const getScheduleTerminalData = createAsyncThunk('customerDashboard/GetCustomerScheduleEquipmentList', async (data) => {
    const { params, filter, userData } = data // ADDED: userData

    const uri = getUriParams(params)
    const filterData = {
        movementType: "schedule", // Customer Dashboard only to show scheduled movement
        yardLocation: filter.yardLocation || "ALL",
        customerName: filter.customerName === "ALL" || filter.customerName === "" ? "" : filter.customerName || "",
        line: filter.line || "",
        referenceNumber: filter.referenceNumber || "",
        equipmentNumber: filter.equipmentNumber || "",
        equipmentStatus: filter.equipmentStatus || "",
        equipmentStatusList: filter.equipmentStatusList || [],
        commodityType: filter.commodityType || "",
        equipmentType: filter.equipmentType || "ALL",
        from: filter.from || null,
        to: filter.to || null,
        etaFrom: filter.etaFrom || null,
        etaTo: filter.etaTo || null,
        arrivalDateFrom: filter.arrivalDateFrom || null,
        arrivalDateTo: filter.arrivalDateTo || null,
        destination: filter.destination || "",
        origin: filter.origin || "",
        cargoType: filter.cargoType || "",
        commodity: filter.commodity || "",
        grade: filter.grade || "",
        lotNumber: filter.lotNumber || "",
        lastTracedLocation: filter.lastTracedLocation || "",
        servingYard: filter.servingYard || "",
        truckingCompany: filter.truckingCompany || "",
        equipmentSize: filter.equipmentSize || "",
        equipmentSizeList: filter.equipmentSizeList || [],
        shipper: getCompanyCode(userData),
        track: filter.track || "",
        vesselName: filter.vesselName || "",
        voyageNumber: filter.voyageNumber || "",
        filterTracingStatusList: filter.filterTracingStatusList || [],
        isOutgated: filter.isOutgated || null,
        filterInvalidSchedule: filter.filterInvalidSchedule || null,
        filterNotTracing: filter.filterNotTracing || null,
        ...(filter.tracingStatus !== 'TRANSIT' ? { [filter.tracingStatus]: filter.tracingDate } : {})
    }

    const response = await axios.post(`${urlEquipmentScheduleService}/GetCustomerScheduleEquipmentList?${uri}`, filterData)

    return {
        params,
        data: response.data.data,
        totalPages: response.data.totalCount,
        filterData
    }
})

// Special Reports - Customer Code
export const getSpecialReport = async (userData) => { // ADDED: userData parameter
    const customerCode = getCompanyCode(userData)
    const response = await axios.get(`${urlReportingService}/GetSpecialReport/${customerCode}`)
    if (response.data && response.data.totalCount > 0) {
        return {
            success: true,
            data: response.data.data
        }
    } else {
        return {
            success: false,
            message: 'Internal Server Error'
        }
    } 
}

// JTW Token
export const getIsValidToken = async () => {
    const response = await axios.get(`${urlAuthService}/IsValidToken`)
    return response.success
}

export const putRefreshToken = async () => {
    const response = await axios.get(`${urlAuthService}/RefreshToken`)
    return response.success
}

// Get Commodties
export const getCommodities = async () => {
    const cargoTypes = await axios.get(`${urlConfigCacheService}/CargoType`)
    const commodityTypes = await axios.get(`${urlConfigCacheService}/CommodityType`)
    const commodities = await axios.get(`${urlConfigCacheService}/CommodityName`)
    const commodityGrades = await axios.get(`${urlConfigCacheService}/CommodityGrade`)
    if (cargoTypes.data || commodityTypes.data || commodities.data || commodityGrades.data) {
        return {
            success: true,
            data: {
                cargoTypes: cargoTypes.data || [],
                commodityTypes: commodityTypes.data || [],
                commodities: commodities.data || [],
                commodityGrades: commodityGrades.data || []
            }
        }
    } else {
        return {
            success: false,
            message: 'Internal Server Error'
        }
    }
}

// Get Source/Destination
export const getRegionCity = async () => {
    const response = await axios.get(`${urlConfigCacheService}/GetAllSelect/RegionCity`)
    if (response.data) {
        return {
            success: true,
            data: response.data.data
        }
    } else {
        return {
            success: false,
            message: 'Internal Server Error'
        }
    }
}

// Distinct Config Cache Values 
export const GetTransloadingConfig = createAsyncThunk('customerDashboard/GetTransloadingConfig', async (data) => {
    const { userData } = data

    const response = await axios.get(`${urlReportingService}/GetTransloadingConfig?customerCode=${getCompanyCode(userData)}`)
    if (response.data) {
        return {
            success: true,
            data: response.data.data
        }
    } else {
        return {
            success: false,
            message: 'Internal Server Error'
        }
    }
})

export const GetCargoInventoryConfig = createAsyncThunk('customerDashboard/GetCargoInventoryConfig', async (data) => {
    const { userData } = data

    const response = await axios.get(`${urlCargoInventoryService}/GetCargoInventoryConfig?customerCode=${getCompanyCode(userData)}`)
    if (response.data) {
        return {
            success: true,
            data: response.data.data
        }
    } else {
        return {
            success: false,
            message: 'Internal Server Error'
        }
    }
})

export const GetInboundCargoConfig = createAsyncThunk('customerDashboard/GetInboundCargoConfig', async (data) => {
    const { userData } = data

    const response = await axios.get(`${urlEquipmentScheduleService}/GetInboundCargoConfig?customerCode=${getCompanyCode(userData)}`)
    if (response.data) {
        return {
            success: true,
            data: response.data.data
        }
    } else {
        return {
            success: false,
            message: 'Internal Server Error'
        }
    }
})

export const customerDashboardSlice = createSlice({
    name: 'customerDashboard',
    initialState: { // Initial State for tabs
        transload: {
            data: [],
            total: 0,
            filter: {},
            params: {},
            loaded: false
        },
        cleanliness: {
            data: [],
            total: 0,
            filter: {},
            params: {},
            loaded: false
        },
        cleanlinessCertificatePreview: {
            data: []
        },
        weightCertificatePreview: {
            data: []
        },
        fforder: {
            data: [],
            total: 0,
            filter: {},
            params: {},
            loaded: false
        },
        cargo: {
            data: [],
            total: 0,
            filter: {},
            params: {},
            loaded: false
        },
        inventory: {
            data: [],
            total: 0,
            filter: {},
            params: {},
            loaded: false
        },
        schedule: {
            data: [],
            total: 0,
            filter: {},
            params: {},
            loaded: false
        },
        specialReportData: [],
        specialReportTotal: 0
    },
    reducers: {},
    extraReducers: builder => {
        builder.addCase(GetAllCustomerTransloadReportList.pending, (state) => {
            state.transload.loaded = false
        })
        .addCase(GetAllCustomerTransloadReportList.fulfilled, (state, action) => {
            state.transload.data = action.payload.data
            state.transload.total = action.payload.totalPages
            state.transload.filter = action.payload.filterData
            state.transload.params = action.payload.params
            state.transload.loaded = true
        })
        .addCase(GetAllCustomerCleanlinessCertificateList.pending, (state) => {
            state.cleanliness.loaded = false
        })
        .addCase(GetAllCustomerCleanlinessCertificateList.fulfilled, (state, action) => {
            state.cleanliness.data = action.payload.data
            state.cleanliness.total = action.payload.totalPages
            state.cleanliness.filter = action.payload.filterData
            state.cleanliness.params = action.payload.params
            state.cleanliness.loaded = true
        })
        .addCase(GetCustomerCleanlinessCertificate.fulfilled, (state, action) => {
            state.cleanlinessCertificatePreview.data = action.payload.data || []
        })
        .addCase(GetCustomerWeightCertificate.fulfilled, (state, action) => {
            state.weightCertificatePreview.data = action.payload.data || []
        })
        .addCase(getFFOrderList.pending, (state) => {
            state.fforder.loaded = false
        })
        .addCase(getFFOrderList.fulfilled, (state, action) => {
            state.fforder.data = action.payload.data
            state.fforder.total = action.payload.totalPages
            state.fforder.filter = action.payload.filterData
            state.fforder.params = action.payload.params
            state.fforder.loaded = true
        })
        .addCase(getCargoInventory.pending, (state) => {
            state.cargo.loaded = false
        })
        .addCase(getCargoInventory.fulfilled, (state, action) => {
            state.cargo.data = action.payload.data
            state.cargo.total = action.payload.totalPages
            state.cargo.filter = action.payload.filterData
            state.cargo.params = action.payload.params
            state.cargo.loaded = true
        })
        .addCase(getMaterialInventory.pending, (state) => {
            state.inventory.loaded = false
        })
        .addCase(getMaterialInventory.fulfilled, (state, action) => {
            state.inventory.data = action.payload.data
            state.inventory.total = action.payload.totalPages
            state.inventory.filter = action.payload.filterData
            state.inventory.params = action.payload.params
            state.inventory.loaded = true
        })
        .addCase(getScheduleTerminalData.pending, (state) => {
            state.schedule.loaded = false
        })
        .addCase(getScheduleTerminalData.fulfilled, (state, action) => {
            state.schedule.data = action.payload.data
            state.schedule.total = action.payload.totalPages
            state.schedule.filter = action.payload.filterData
            state.schedule.params = action.payload.params
            state.schedule.loaded = true
        })
        .addCase(GetTransloadingConfig.fulfilled, (state, action) => {
            state.transload.config = action.payload.data
        })
        .addCase(GetCargoInventoryConfig.fulfilled, (state, action) => {
            state.cargo.config = action.payload.data
        })
        .addCase(GetInboundCargoConfig.fulfilled, (state, action) => {
            state.schedule.config = action.payload.data
        })
    }
})

export default customerDashboardSlice.reducer